/**
 * Browser-side media helpers for the phone app. Everything here runs on the phone: a gallery
 * video is read through a blob: URL, its duration/size come from a metadata-only <video>, and
 * poster frames are grabbed off a hidden <video> onto a canvas — no upload, no server.
 */

const DEFAULT_PROBE_TIMEOUT = 20000;

/** Read duration + pixel size of a video url. Resolves { duration, width, height }. */
export function probeVideo(url, { timeoutMs = DEFAULT_PROBE_TIMEOUT } = {}) {
  return new Promise((resolve, reject) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    v.playsInline = true;
    let done = false;
    const finish = (fn, val) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      v.onloadedmetadata = null;
      v.ondurationchange = null;
      v.onerror = null;
      v.removeAttribute("src");
      try { v.load(); } catch (e) { /* ignore */ }
      fn(val);
    };
    const timer = setTimeout(() => finish(reject, new Error("timeout")), timeoutMs);
    const settle = () => {
      if (!Number.isFinite(v.duration) || v.duration <= 0) return false;
      finish(resolve, { duration: v.duration, width: v.videoWidth || 0, height: v.videoHeight || 0 });
      return true;
    };
    v.onloadedmetadata = () => {
      if (settle()) return;
      // Some recordings report Infinity until you seek past the end — nudge, then re-read.
      v.ondurationchange = () => settle();
      try { v.currentTime = 1e6; } catch (e) { /* ignore */ }
    };
    v.onerror = () => finish(reject, new Error("unreadable"));
    v.src = url;
  });
}

/**
 * One hidden <video> + canvas that grabs JPEG posters, one at a time, in queue order.
 * `enqueue(key, url, time)` resolves a data: URL ("" on failure — never throws). Keys are the
 * CLIP IDS, so the caller keeps posters in a per-id map, exactly like the web ET's
 * `clipThumbnails` (never inside the rows).
 */
export function createPosterExtractor({ width = 240, quality = 0.62, seekTimeoutMs = 4000 } = {}) {
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.crossOrigin = "anonymous";
  const canvas = document.createElement("canvas");
  const queue = [];
  let running = false;
  let destroyed = false;
  let loadedUrl = null;

  const waitEvent = (target, ok, ms) =>
    new Promise((resolve) => {
      let t;
      const off = () => { target.removeEventListener(ok, onOk); target.removeEventListener("error", onErr); clearTimeout(t); };
      const onOk = () => { off(); resolve(true); };
      const onErr = () => { off(); resolve(false); };
      target.addEventListener(ok, onOk);
      target.addEventListener("error", onErr);
      t = setTimeout(() => { off(); resolve(false); }, ms);
    });

  const load = async (url) => {
    if (loadedUrl === url && video.readyState >= 1) return true;
    loadedUrl = url;
    video.src = url;
    const ok = await waitEvent(video, "loadedmetadata", seekTimeoutMs * 2);
    return ok && video.readyState >= 1;
  };

  const grab = async (job) => {
    if (!(await load(job.url))) return "";
    const dur = Number.isFinite(video.duration) ? video.duration : job.time + 1;
    // A hair after the cut so the first frame isn't the black keyframe of a hard cut.
    const t = Math.min(Math.max(0, job.time + 0.08), Math.max(0, dur - 0.05));
    const seeked = waitEvent(video, "seeked", seekTimeoutMs);
    try { video.currentTime = t; } catch (e) { return ""; }
    if (!(await seeked)) return "";
    // Android's WebView fires `seeked` before the new frame is painted, so a canvas grab right
    // here can return the PREVIOUS frame (seen on the emulator: clip 2 got clip 1's poster).
    // Wait for the frame to actually be presented; fall back to a short delay.
    await new Promise((resolve) => {
      let done = false;
      const finish = () => { if (!done) { done = true; resolve(); } };
      if (typeof video.requestVideoFrameCallback === "function") {
        video.requestVideoFrameCallback(finish);
        setTimeout(finish, 350);
      } else {
        setTimeout(finish, 120);
      }
    });
    const vw = video.videoWidth || 16, vh = video.videoHeight || 9;
    const w = width, h = Math.max(1, Math.round((width * vh) / vw));
    canvas.width = w; canvas.height = h;
    try {
      canvas.getContext("2d").drawImage(video, 0, 0, w, h);
      return canvas.toDataURL("image/jpeg", quality);
    } catch (e) {
      return "";
    }
  };

  const pump = async () => {
    if (running) return;
    running = true;
    while (queue.length && !destroyed) {
      const job = queue.shift();
      let out = "";
      try { out = await grab(job); } catch (e) { out = ""; }
      job.resolve(out);
    }
    running = false;
  };

  return {
    /** Queue a poster. `front: true` jumps the queue (what's on screen goes first). */
    enqueue(key, url, time, { front = false } = {}) {
      return new Promise((resolve) => {
        if (destroyed) return resolve("");
        const job = { key, url, time, resolve };
        if (front) queue.unshift(job); else queue.push(job);
        pump();
      });
    },
    /** Move already-queued keys to the front, in the given order. */
    prioritize(keys) {
      const want = new Set((keys || []).map(String));
      const first = [], rest = [];
      queue.forEach((j) => (want.has(String(j.key)) ? first : rest).push(j));
      queue.length = 0;
      queue.push(...first, ...rest);
    },
    pending() { return queue.length + (running ? 1 : 0); },
    cancelAll() { queue.splice(0).forEach((j) => j.resolve("")); },
    destroy() {
      destroyed = true;
      this.cancelAll();
      video.removeAttribute("src");
      try { video.load(); } catch (e) { /* ignore */ }
    },
  };
}
